import React from 'react';

import _ from 'lodash';
import { ClinicResponse } from 'src/common/types/ClinicResponse';
import { AIOutput, SaveSheet } from 'src/common/types/SaveSheet';

import {
  useCheckCodeCombinationsMutation,
  useLazySearchQuery,
} from '../../common/apis/FlorenceApi';
import { useAppSelector, useAppDispatch } from '../../common/hooks/reduxHooks';
import { clearAllFilters } from '../../common/stores/modelSlice';
import { ICD10Code, SelectableCode } from '../../common/types/Codes';
import { Search } from '../../common/types/Search';

export const useCodes = (
  sheet: SaveSheet | null,
  searchText: string,
  searchType: 'text' | 'scan',
  limit: number,
  updateNote: (
    note: SaveSheet,
    showConfirmationMessage: boolean,
    updateLocally: boolean
  ) => Promise<void>,
  isSmartLaunch = false,
  forceSurgeryEngine = false
) => {
  const dispatch = useAppDispatch();

  // Filters
  const { filterInclude, filterExclude } = useAppSelector(
    (state) => state.model
  );
  const [include, setInclude] = React.useState(filterInclude);
  const [exclude, setExclude] = React.useState(filterExclude);
  const [filtersLength, setFiltersLength] = React.useState(
    include.length + exclude.length
  );

  const applyFilters = React.useCallback(() => {
    setInclude(filterInclude);
    setExclude(filterExclude);
  }, [filterExclude, filterInclude]);

  const clearFilters = React.useCallback(() => {
    dispatch(clearAllFilters());
    setInclude([]);
    setExclude([]);
  }, [dispatch]);

  // Clinic Response for Clinic Notes
  const [clinicResponse, setClinicResponse] =
    React.useState<ClinicResponse | null>(null);

  const [submitSearch, { data: results, isFetching: isLoading }] =
    useLazySearchQuery();
  const [incorrectCodeCombinations, setIncorrectCodeCombinations] =
    React.useState(false);

  const [checkCodeCombinations] = useCheckCodeCombinationsMutation();

  const [argsInitialized, setArgsInitialized] = React.useState(false);

  const [suggestedICD10, setSuggestedICD10] = React.useState<ICD10Code[]>([]);
  const [suggestedModifiers, setSuggestedModifiers] = React.useState<
    { cpt_code: string; modifier: string }[]
  >([]);
  const [suggestedUnits, setSuggestedUnits] = React.useState<
    { cpt_code: string; unit: number }[]
  >([]);

  const [AIOutput, setAIOutput] = React.useState<AIOutput | null>(null);

  const [importantCodes, setImportantCodes] = React.useState<string[]>([]);

  // Search Args triggering refetch
  const searchArgs: Search = React.useMemo(() => {
    return {
      text: searchText,
      sheetId: sheet?.sheetId,
      searchType,
      includeTerms: include,
      excludeTerms: exclude,
      limit,
      smartLaunch: isSmartLaunch,
      forceSurgeryEngine: forceSurgeryEngine,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText, searchType, include, exclude, limit, isSmartLaunch]);

  // The main codes state
  const [codes, setCodes] = React.useState<SelectableCode[]>([]);

  const isArrayEqual = (x: any[], y: any[]) => {
    return _(x).xorWith(y, _.isEqual).isEmpty();
  };

  const updateCodes = (newCodes: SelectableCode[]) => {
    if (isArrayEqual(newCodes, codes)) return;
    let sortedCodes = newCodes;

    // sort the codes and keep primary codes at the top
    sortedCodes = _.sortBy(sortedCodes, [
      (c) => (c.isPrimaryCode ? 0 : 1),
      (c) => (c.manuallyAdded ? 0 : 1),
    ]);

    sortedCodes = _.uniqBy(sortedCodes, 'code');
    setCodes(sortedCodes);
  };

  // Main function refetching the codes
  const refetchCodes = React.useCallback(async () => {
    if (_.isEmpty(searchArgs.text)) return;
    try {
      const response = await submitSearch({ ...searchArgs }, false).unwrap();
      if (response.results.code_details) {
        // Clinic Note
        const returnedClinicResponse = {
          data_response: response.results.data_response ?? '',
          final_code: response.results.final_code ?? '',
          problem_response: response.results.problem_response ?? '',
          risk_response: response.results.risk_response ?? '',
          status_response: response.results.status_response ?? 'unknown',
          cpt_response: response.results.cpt_response ?? '',
          icd_response: response.results.icd_response ?? '',
          hcpc_response: response.results.hcpc_response ?? '',
          code_details: response.results.code_details ?? null,
        };

        setClinicResponse(returnedClinicResponse);
      }

      if (response.results.icd10_codes) {
        setSuggestedICD10(
          response.results.icd10_codes.map((c) => {
            const isSelected = response.results.important_codes
              ? response.results.important_codes.includes(c.code)
              : false;

            return {
              code: c.code,
              description: c.description,
              selected: isSelected,
              source: 'galeai',
            };
          })
        );
      }

      // Extended surgery engine
      if (response.results.cpt_modifiers) {
        setSuggestedModifiers(response.results.cpt_modifiers);
      }

      if (response.results.cpt_units) {
        setSuggestedUnits(response.results.cpt_units);
      }

      if (
        response.results.icd10_text ||
        response.results.cpt_modifiers_text ||
        response.results.cpt_units_text ||
        response.results.grants_choice
      ) {
        setAIOutput({
          icd10: response.results.icd10_text ?? '',
          modifiers: response.results.cpt_modifiers_text ?? '',
          units: response.results.cpt_units_text ?? '',
          grants_choice: response.results.grants_choice ?? '',
        });
      }

      if (response.results.important_codes) {
        setImportantCodes(response.results.important_codes);
      }
    } catch (error: unknown) {
      console.log(error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchArgs]);

  // Initialize the codes from the sheet
  React.useEffect(() => {
    if (sheet) {
      if (sheet.clinicNoteResults) {
        setClinicResponse(sheet.clinicNoteResults);
      }

      if (sheet.aiOutput) {
        setAIOutput(sheet.aiOutput);
      }

      if (sheet.sheetCodes && sheet.sheetCodes.length) {
        updateCodes(
          sheet.sheetCodes.map((c) => {
            return {
              ...c,
              selected: true,
              from: isSmartLaunch ? ('athena' as const) : ('user' as const),
            };
          })
        );

        // If the sheet has codes, refetch codes only if it's a smart launch
        if (isSmartLaunch) refetchCodes();
      } else {
        // If the sheet has no codes, refetch codes
        refetchCodes();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sheet]);

  React.useEffect(() => {
    // Trigger refetchCodes if searchArgs is changed
    // only after the initial render of the component

    if (!argsInitialized) {
      setArgsInitialized(true);
    } else {
      refetchCodes();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchArgs]);

  // Update the codes when the results are fetched
  React.useEffect(() => {
    if (!argsInitialized && isLoading) return;

    let primaryCode: SelectableCode | null = null;

    if (results?.results?.code_details && results?.results?.codes?.length) {
      primaryCode = {
        ...results.results?.code_details,
        selected: true,
        manuallyAdded: false,
        order: 999,
        isPrimaryCode: true,
        from: 'galeai',
        modifiers: getSuggestedModifiers(results.results?.code_details.code),
        multiplier: getSuggestedUnits(results.results?.code_details.code),
        icd10: [],
      };
    }

    let auxCodes = primaryCode ? [primaryCode] : results?.results?.codes ?? [];

    // Add ICD-10 codes if it's not a smart launch and we have suggested ICD-10 codes from the extended engine
    if (
      !isSmartLaunch &&
      results?.results?.icd10_codes &&
      results?.results?.icd10_codes.length > 0
    ) {
      const icd10Codes: SelectableCode[] = results.results.icd10_codes.map(
        (icd10, index) => ({
          id: index.toString(),
          code: icd10.code,
          description: icd10.description,
          manuallyAdded: false,
          selected: findIfImportant(icd10.code),
          order: 999,
          isPrimaryCode: false,
          from: 'galeai',
          multiplier: 1,
          modifiers: [],
          icd10: [],
        })
      );

      auxCodes = [...auxCodes, ...icd10Codes];
    }

    if (results?.results?.hcpcs_codes) {
      for (const hcpcsCode of results?.results?.hcpcs_codes) {
        auxCodes.push({
          id: '0',
          code: hcpcsCode.code,
          description: hcpcsCode.description,
          manuallyAdded: false,
          selected: true,
          order: 999,
          isPrimaryCode: false,
          from: 'galeai',
          multiplier: 1,
          modifiers: [],
          icd10: [],
        });
      }
    }

    if (suggestedModifiers) {
      auxCodes = auxCodes.map((c) => {
        return {
          ...c,
          modifiers: getSuggestedModifiers(c.code),
        };
      });
    }

    if (suggestedUnits) {
      auxCodes = auxCodes.map((c) => {
        return {
          ...c,
          multiplier: getSuggestedUnits(c.code),
        };
      });
    }

    const codesFromGaleAI: SelectableCode[] = _.chain(auxCodes)
      .map((c) =>
        _.assign({}, c, {
          selected:
            _.has(c, 'selected') && (c as SelectableCode).selected === true
              ? true
              : findIfImportant(c.code),
        })
      )
      .value();

    const isPrimaryCode = (code: string) => {
      // E&M coding involves use of CPT codes ranging from 99202 to 99499.
      const parsedCode = parseInt(code);
      if (parsedCode >= 99202 && parsedCode <= 99499) {
        return true;
      }

      return false;
    };

    if (isSmartLaunch) {
      for (const encounterCode of sheet?.sheetCodes ?? []) {
        // if the code exists in the results, update the "from" field
        // otherwise add the code to the results

        const codeIndex = codesFromGaleAI.findIndex(
          (c) => c.code === encounterCode.code
        );

        const isPrimary = isPrimaryCode(encounterCode.code);

        if (codeIndex !== -1) {
          // get it from the old "codes" array
          const oldCode = codes.find((c) => c.code === encounterCode.code);

          const from = 'confirmed';
          const multiplier =
            oldCode?.multiplier ?? encounterCode.multiplier ?? 1;
          const modifiers = oldCode?.modifiers ?? encounterCode.modifiers ?? [];
          const icd10 = oldCode?.icd10 ?? encounterCode.icd10 ?? [];

          codesFromGaleAI[codeIndex].selected = true;
          codesFromGaleAI[codeIndex].from = from;
          codesFromGaleAI[codeIndex].multiplier = multiplier;
          codesFromGaleAI[codeIndex].modifiers = modifiers;
          codesFromGaleAI[codeIndex].isPrimaryCode = isPrimary;
          codesFromGaleAI[codeIndex].icd10 = icd10;
        } else {
          codesFromGaleAI.push({
            ...encounterCode,
            selected: true,
            manuallyAdded: encounterCode.manuallyAdded ?? false,
            order: 1,
            isPrimaryCode: isPrimary,
            from: encounterCode.from ?? 'athena',
            icd10: encounterCode.icd10 ?? [],
          });
        }
      }
    }

    updateCodes(codesFromGaleAI);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [results]);

  React.useEffect(() => {
    setFiltersLength(include.length + exclude.length);
  }, [include, exclude]);

  // Code updating functions
  const selectModifiers = (code: SelectableCode, modifiers: string[]) => {
    updateCodes(
      codes.map((c) => {
        if (c.code !== code.code) return c;
        else return { ...c, modifiers: modifiers };
      })
    );
  };

  const selectMultiplier = (code: SelectableCode, multiplier: number) => {
    updateCodes(
      codes.map((c) => {
        if (c.code !== code.code) return c;
        else return { ...c, multiplier: multiplier };
      })
    );
  };

  const selectCode = (code: SelectableCode) => {
    updateCodes(
      codes.map((c) => {
        if (c.code !== code.code) return c;
        else
          return {
            ...c,
            // toggle the selected state
            selected: !c.selected,
            // reset the invalidCombos if the code is unselected
            invalidCombos: c.selected ? [] : code.invalidCombos,
          };
      })
    );
  };

  const selectAllCodes = () => {
    updateCodes(
      codes.map((code) => {
        return { ...code, selected: true };
      })
    );
  };

  const clearSelectedCodes = () => {
    updateCodes(
      codes.map((code) => {
        return { ...code, selected: false };
      })
    );
  };

  const addManualCode = (code: SelectableCode) => {
    code.from = 'galeai';
    const codeIndex = codes.findIndex((c) => c.code === code.code);
    if (codeIndex !== -1) {
      updateCodes(
        codes.map((c) => {
          if (c.code !== code.code) return c;
          else return code;
        })
      );
    } else {
      updateCodes([code, ...codes]);
    }
  };

  const flagCode = (code: SelectableCode) => {
    updateCodes(
      codes.map((c) => {
        if (c.code !== code.code) return c;
        else
          return {
            ...c,
            isFlagged: !c.isFlagged,
          };
      })
    );
  };

  const selectedCodes = React.useMemo(
    () => codes.filter((c) => c.selected),
    [codes]
  );

  // Check Code Combinations Compatibility
  React.useEffect(() => {
    if (selectedCodes.length < 2) {
      // reset invalid combos
      setIncorrectCodeCombinations(false);
      updateCodes(
        codes.map((c) => {
          c.invalidCombos = [];
          return c;
        })
      );
      return;
    }

    checkCodeCombinations(selectedCodes.map((c) => c.code))
      .unwrap()
      .then((combos) => {
        const incorrect =
          combos.filter((c) => c.status === 'incorrect').length > 0;
        setIncorrectCodeCombinations(incorrect);

        const newCodes = codes.map((c) => {
          for (const combo of combos) {
            if (c.code === combo.code) {
              c.invalidCombos = combo.reasons;
            }
          }
          return c;
        });

        updateCodes(newCodes);
      })
      .catch((error) => {
        console.log(error);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCodes, codes, checkCodeCombinations]);

  const updateICD10 = (code: SelectableCode, newIcd10: ICD10Code[]) => {
    updateCodes(
      codes.map((c) => {
        if (c.code !== code.code) return c;
        else
          return {
            ...c,
            code: c.code,
            icd10: [...newIcd10],
          };
      })
    );
  };

  const getSuggestedModifiers = (code: string) => {
    return suggestedModifiers
      .filter((m) => m.cpt_code === code)
      .map((m) => m.modifier);
  };

  const getSuggestedUnits = (code: string) => {
    const suggestedUnit = suggestedUnits.find((m) => m.cpt_code === code);
    if (suggestedUnit) {
      return suggestedUnit.unit;
    }

    return 1;
  };

  const findIfImportant = (code: string) => {
    if (!importantCodes) return false;
    return importantCodes.includes(code);
  };

  return {
    codes,
    selectedCodes,
    filtersLength,
    isLoading: isLoading,
    clinicResponse,
    incorrectCodeCombinations,
    suggestedICD10,
    refetchCodes,
    selectModifiers,
    selectMultiplier,
    applyFilters,
    clearFilters,
    clearSelectedCodes,
    selectAllCodes,
    selectCode,
    addManualCode,
    flagCode,
    updateICD10,
    AIOutput,
  };
};
